// Asking the market's addresses. A failure says what happened in words for the person; the page decides what to do about it.

export class ApiFailure extends Error {
  readonly status: number;
  readonly code: string;
  readonly body: { suggestions?: string[]; problems?: { path: string; message: string }[]; retryable?: boolean };
  constructor(status: number, code: string, message: string, body: ApiFailure['body'] = {}) {
    super(message);
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

export const SESSION_ENDED = 'market:session-ended';

async function failure(response: Response): Promise<ApiFailure> {
  const data = (await response.json().catch(() => null)) as { error?: string; message?: string; suggestions?: string[]; problems?: { path: string; message: string }[]; retryable?: boolean } | null;
  const failed = new ApiFailure(response.status, data?.error ?? 'unknown', data?.message ?? '市场暂时无法处理这个请求，请稍后再试。', {
    ...(data?.suggestions ? { suggestions: data.suggestions } : {}), ...(data?.problems ? { problems: data.problems } : {}), ...(data?.retryable ? { retryable: true } : {}),
  });
  if (failed.status === 401 && failed.code === 'unauthenticated') window.dispatchEvent(new Event(SESSION_ENDED));
  return failed;
}

async function send(path: string, init: RequestInit): Promise<Response> {
  try {
    return await fetch(path, { credentials: 'same-origin', ...init });
  } catch {
    throw new ApiFailure(0, 'network', '无法连接到市场，请检查网络后重试。');
  }
}

export async function api<T>(method: string, path: string, body?: unknown): Promise<T> {
  const response = await send(path, {
    method,
    ...(body === undefined ? {} : { headers: { 'content-type': 'application/json' }, body: JSON.stringify(body) }),
  });
  if (!response.ok) throw await failure(response);
  return (await response.json()) as T;
}

export const get = <T>(path: string) => api<T>('GET', path);

/** An uploaded icon: the PNG itself. */
export async function uploadIcon(png: Blob): Promise<{ key: string }> {
  const response = await send('/api/admin/icons', { method: 'POST', headers: { 'content-type': 'image/png' }, body: png });
  if (!response.ok) throw await failure(response);
  return (await response.json()) as { key: string };
}

/** A package: fetched first, so a version taken down in the meantime says so instead of saving an error page. */
export async function fetchFile(path: string): Promise<{ blob: Blob; name: string }> {
  const response = await send(path, { method: 'GET' });
  if (!response.ok) throw await failure(response);
  const disposition = response.headers.get('content-disposition') ?? '';
  const name = /filename="([^"]+)"/.exec(disposition)?.[1] ?? 'package.tar.gz';
  return { blob: await response.blob(), name };
}
